import gql from 'graphql-tag';
import className from 'classnames/bind';
import { QuickLinksFragment } from '@graphqlTypes';
import { Container, RevealWrapper, HeadlineAnim, RhLink, Image } from '@components';
import { getHighlightMarkup } from '@/core/utils';

import { useRef } from "react";
import { useInView } from "motion/react";
import { useViewSize } from "@/providers/ViewSizeContext";
import { AnimVals } from '@/utils/animUtils';

import styles from './QuickLinks.module.scss';
const cx = className.bind(styles);

const QuickLink = ({ link }: { link: QuickLinksFragment['links'][number]}) => {
  const { label, icon, externalLink, page, linkType } = link;
  const [type] = linkType;
  const isExternal = type === 'external';
  const url = (isExternal ? externalLink : page?.node?.uri) || "";
  const target = isExternal ? "_blank" : undefined;
  return (
    <li className={cx("link-wrapper")}>
      <RhLink href={url} target={target} className={cx("link")}>
        {icon && (
          <div className={cx("icon")}>
            <Image image={icon} alt="" />
          </div>
        )}
        <div className={cx("content")}>
          <p className={cx("label", "title-lc-s")}>{label}</p>
        </div>
      </RhLink>
    </li>
  );
}

export default function QuickLinks(props: QuickLinksFragment) {
  const { links, headline, subhead } = props;
  const headlineMarkup = headline ? getHighlightMarkup(headline) : null;
  const showHeader = Boolean(headlineMarkup || subhead);

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const isRevealEnabled = true;
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Render

  return (
    <section className={cx(["quick-links"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          {showHeader && (
            <div className={cx("header")}>
              {headlineMarkup && (
                <HeadlineAnim
                  tag="h2"
                  text={headlineMarkup}
                  className={cx("title-uc-l")}
                  isRevealEnabled={isRevealEnabled}
                  isInView={isInView}
                />
              )}
              {subhead && <p className={cx("text-m", "subhead")}>{subhead}</p>}
            </div>
          )}
          <ul className={cx("links")}>
            {links.map((link, index) => (
              <QuickLink key={index} link={link} />
            ))}
          </ul>
        </Container>
      </RevealWrapper>
    </section>
  );
}

QuickLinks.fragments = {
  entry: gql`
    fragment QuickLinks on FlexiblePageContentContentQuickLinksLayout {
      headline
      subhead
      links {
        icon {
          ...ACFMedia
        }
        label
        linkType
        externalLink
        page {
          node {
            uri
          }
        }
      }
    }
  `,
};
